import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { API_ERROR_CODE, type ApiFailure, type ApiSuccess } from '@dsv/shared';
import { t } from '@/i18n';
import { translateServerText } from '@/utils/server-text';
import { tokenStorage } from './token-storage';
import { resolveApiBase } from './api-base';

/** 前端统一的错误对象。业务代码 catch 到的都是它。 */
export class RequestError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: { field: string; message: string }[];
  readonly requestId?: string;

  constructor(init: {
    code: string;
    status: number;
    message: string;
    details?: { field: string; message: string }[];
    requestId?: string;
  }) {
    super(init.message);
    this.name = 'RequestError';
    this.code = init.code;
    this.status = init.status;
    this.details = init.details;
    this.requestId = init.requestId;
  }

  /**
   * 按错误码取本地化文案；没有对应文案时退回后端的 message。
   * 校验错误带了逐字段原因（如「填了单价就要填币种」）时接在后面 —— 只说「提交的内容有误」，
   * 用户不知道该改哪一格。
   */
  get localizedMessage(): string {
    const key = `error.${this.code}`;
    const translated = t(key);
    // 后端消息可能是 @key|参数 形态（韩文第一层）——按当前语言翻译，原样则回退
    const base = this.message.startsWith('@') || translated === key ? translateServerText(this.message) : translated;
    const reasons = [
      ...new Set((this.details ?? []).map((d) => translateServerText(d.message ?? '')).filter(Boolean)),
    ];
    return reasons.length > 0 ? `${base}：${reasons.slice(0, 3).join('；')}` : base;
  }
}

const apiBase = resolveApiBase(import.meta.env.BASE_URL, import.meta.env.VITE_API_BASE_URL);

const http: AxiosInstance = axios.create({
  baseURL: apiBase,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * 会走 SAP 接口的请求要用的超时。
 *
 * SAP PI 的同步调用要 ~35 秒（实测 SP0110 34.5s），默认 30 秒会**在后端算完之前**
 * 把请求掐掉，界面上看到的是一句莫名其妙的超时。
 * 后端 `EADS_TIMEOUT_MS` 默认 60 秒，这里留够余量对齐它。
 */
export const SAP_TIMEOUT_MS = 90_000;

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccess();
  if (token && !config.headers.has('Authorization')) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// ---- access token 静默刷新 ----
// 令牌过期时，第一个请求去刷新，其余请求排队等它，刷新完再一起重放。
// 否则页面上多个并发请求会各自发一次刷新，互相把对方刚拿到的令牌作废。
let refreshing: Promise<string> | null = null;

/** 登出回调由 auth store 注册，避免这里直接依赖 store 造成循环依赖 */
let onSessionExpired: (() => void) | null = null;
export function registerSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStorage.getRefresh();
  if (!refreshToken) throw new Error('no refresh token');

  // 用裸 axios 发，避开本实例的拦截器，否则刷新失败会再次触发刷新形成死循环
  const { data } = await axios.post<ApiSuccess<{ accessToken: string; refreshToken: string }>>(
    `${apiBase}/auth/refresh`,
    { refreshToken },
  );

  tokenStorage.set(data.data.accessToken, data.data.refreshToken);
  return data.data.accessToken;
}

/** 把失败响应体统一还原成信封对象；Blob 体（附件下载出错时）先读成文本再解析。 */
async function normalizeFailure(data: unknown): Promise<ApiFailure | undefined> {
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text()) as ApiFailure;
    } catch {
      return undefined;
    }
  }
  return data as ApiFailure | undefined;
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiFailure>) => {
    const config = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const status = error.response?.status ?? 0;

    // 网络层错误（断网、超时）
    if (!error.response) {
      throw new RequestError({
        code: 'NETWORK',
        status: 0,
        message: t('error.NETWORK'),
      });
    }

    // responseType: 'blob' 的请求（附件下载）出错时，错误体也会是 Blob，
    // 直接读 failure.error.code 会拿到 undefined —— 那样令牌过期就走不到下面的
    // 静默刷新分支，而会被当成普通 401 把用户踢去登录页。先还原成 JSON。
    const failure = await normalizeFailure(error.response.data);

    // 有响应但不是我们的信封格式，说明这个响应不是后端业务代码产生的 ——
    // 典型场景是后端没起来：开发期 vite 代理会返回 500，生产环境 nginx 会返回 502/504。
    // 这类情况报「服务器异常」会误导人排查后端代码，统一按连不上后端提示。
    if (failure?.success !== false && status >= 500) {
      throw new RequestError({
        code: 'NETWORK',
        status,
        message: t('error.NETWORK'),
      });
    }

    const isExpired = failure?.error?.code === API_ERROR_CODE.TOKEN_EXPIRED;
    const isRefreshCall = config?.url?.includes('/auth/refresh');

    if (isExpired && config && !config._retried && !isRefreshCall) {
      config._retried = true;
      try {
        refreshing ??= refreshAccessToken().finally(() => {
          refreshing = null;
        });
        const newToken = await refreshing;
        config.headers.set('Authorization', `Bearer ${newToken}`);
        return http.request(config);
      } catch {
        tokenStorage.clear();
        onSessionExpired?.();
        throw new RequestError({
          code: API_ERROR_CODE.UNAUTHORIZED,
          status: 401,
          message: t('error.UNAUTHORIZED'),
        });
      }
    }

    // 未登录 / 刷新本身失败：直接踢回登录页
    if (status === 401) {
      tokenStorage.clear();
      onSessionExpired?.();
    }

    throw new RequestError({
      code: failure?.error?.code ?? API_ERROR_CODE.INTERNAL_ERROR,
      status,
      message: failure?.error?.message ?? t('error.UNKNOWN'),
      details: failure?.error?.details,
      requestId: failure?.requestId,
    });
  },
);

/**
 * 请求封装：自动拆掉 { success, data, requestId } 信封，业务代码直接拿到 data。
 * 需要 requestId 的场景（如上报）从 RequestError 上取。
 */
export const api = {
  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const res = await http.get<ApiSuccess<T>>(url, { params });
    return res.data?.data;
  },
  async post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const res = await http.post<ApiSuccess<T>>(url, body, config);
    return res.data?.data;
  },
  async put<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const res = await http.put<ApiSuccess<T>>(url, body, config);
    return res.data?.data;
  },
  async patch<T>(url: string, body?: unknown): Promise<T> {
    const res = await http.patch<ApiSuccess<T>>(url, body);
    return res.data?.data;
  },
  async delete<T = void>(url: string, body?: unknown): Promise<T> {
    const res = await http.delete<ApiSuccess<T>>(url, { data: body });
    return res.data?.data;
  },
};

export { http };
